import { FC, JSX, useEffect, useState } from 'react';
import { Styled } from '.';
import Image from 'next/image';
import { Button, Modal, Radio } from 'components/core';
import { useI18n } from 'contexts/i18n';
import { ChangeSubscriptionPaymentModalProps } from './ChangeSubscriptionPaymentModal.types';
import {
  useAccountDetails,
  useDoctorProfile,
  usePayments,
} from '../../../reactQuery/queries';
import { CreditCardClientData } from 'types';
import {
  getCreditCardLogo,
  isPayByInvoiceNotEligible,
  isPaymentOptionAvailable,
  reorderCreditCards,
} from 'components/OrderPayment/OrderPayment.helper';
import { AiOutlinePlusCircle } from 'react-icons/ai';
import getConfig from 'next/config';
import { LoadingSpinner, LoadingWrapper } from 'styles/sharedStyles';
import { MdClose } from 'react-icons/md';
import { useUpdateSubscriptionPayment } from 'reactQuery/mutations';
import { useReadOnlyUser } from 'utils/hooks';

const ChangeSubscriptionPaymentModal = ({
  subscriptionId,
  cardToken,
  onClose,
}: ChangeSubscriptionPaymentModalProps) => {
  const {
    data: paymentMethods,
    isLoading: isCreditCardLoading,
    isIdle,
    isError,
  } = usePayments();
  const { publicRuntimeConfig } = getConfig();
  const { data: doctorProfile } = useDoctorProfile();
  const { data: accountDetails } = useAccountDetails();
  const { isReadOnlyUser } = useReadOnlyUser();

  const [selectedCreditCardToken, setSelectedCreditCardToken] = useState<
    string | null
  >(null);
  const [isPayByInvoiceSelected, setIsPayByInvoiceSelected] = useState<boolean>(
    selectedCreditCardToken === null
  );

  const i18n = useI18n();

  const updateSubscriptionPaymentMutation = useUpdateSubscriptionPayment();

  const handleUpdateSubscriptionPaymentMethod = async () => {
    await updateSubscriptionPaymentMutation.mutateAsync({
      subscriptionId: subscriptionId,
      cardToken: selectedCreditCardToken,
    });
    onClose();
  };

  useEffect(() => {
    if (paymentMethods && paymentMethods.creditCards?.length) {
      if (
        paymentMethods.creditCards.some(
          card => card.json_data.ar_card_number === cardToken
        )
      ) {
        setSelectedCreditCardToken(cardToken);
        setIsPayByInvoiceSelected(false);
      }
    }
  }, [paymentMethods]);

  const handleCreditCardChange = (creditCard: CreditCardClientData) => {
    setSelectedCreditCardToken(creditCard.json_data.ar_card_number);
    setIsPayByInvoiceSelected(false);
  };

  const handlePayByInvoice = () => {
    setIsPayByInvoiceSelected(true);
    setSelectedCreditCardToken(null);
  };

  if (isCreditCardLoading || isIdle) {
    return (
      <Modal overlaysize="medium">
        <Styled.CartItemContainer>
          <LoadingWrapper>
            <LoadingSpinner width="25px" height="25px" isPrimary={true} />
          </LoadingWrapper>
        </Styled.CartItemContainer>
      </Modal>
    );
  }

  if (isError || !paymentMethods) {
    return (
      <Modal>
        <Styled.CartItemContainer>
          <Styled.Title>{i18n['Error loading payment methods']}</Styled.Title>
        </Styled.CartItemContainer>
      </Modal>
    );
  }

  return (
    <Modal overlaysize="medium">
      <Styled.CartItemContainer>
        <Styled.ModalHeader>
          <Styled.Title>{i18n['Change Payment Method']}</Styled.Title>
          <Styled.CloseButton onClick={onClose}>
            <MdClose size={24} />
          </Styled.CloseButton>
        </Styled.ModalHeader>
        <>
          {accountDetails &&
            accountDetails.isDso &&
            !accountDetails.isDsoMandatoryCardPaymentRequired && (
              <Styled.PayByInvoiceContainer
                isSelected={selectedCreditCardToken === null}
                onClick={handlePayByInvoice}
              >
                <Radio
                  checked={selectedCreditCardToken === null}
                  onChange={handlePayByInvoice}
                />
                <Image
                  src={getCreditCardLogo('invoice')}
                  alt="logo"
                  width={40}
                  height={40}
                />
                <Styled.PayByInvoiceTitle>
                  {i18n['Pay By Invoice']}
                </Styled.PayByInvoiceTitle>
              </Styled.PayByInvoiceContainer>
            )}
          {reorderCreditCards(paymentMethods.creditCards)?.map(creditCard => {
            return (
              <Styled.CreditCardContainer
                key={creditCard.id}
                isSelected={
                  selectedCreditCardToken ===
                  creditCard.json_data.ar_card_number
                }
                onClick={() => handleCreditCardChange(creditCard)}
              >
                <Styled.CardDetailsWrapper>
                  <Radio
                    checked={
                      selectedCreditCardToken ===
                      creditCard.json_data.ar_card_number
                    }
                    onChange={() => handleCreditCardChange(creditCard)}
                  />
                  <Image
                    src={getCreditCardLogo(creditCard.cardType)}
                    alt="logo"
                    width={40}
                    height={40}
                  />
                  <Styled.CardHolderName>
                    {creditCard.name}
                  </Styled.CardHolderName>
                  <Styled.LastDigits>
                    {`${i18n['ending in']} ${creditCard.last4Digit}`}{' '}
                  </Styled.LastDigits>
                  <Styled.Expiry>{`${i18n['Exp:']} ${creditCard.expiry}`}</Styled.Expiry>
                </Styled.CardDetailsWrapper>
                {creditCard.default && (
                  <Styled.DefaultCard>{i18n['Default']}</Styled.DefaultCard>
                )}
              </Styled.CreditCardContainer>
            );
          })}
        </>

        {!isPaymentOptionAvailable(paymentMethods) && (
          <Styled.AddButtonWrapper>
            <Button
              onClick={() => {
                window.open(publicRuntimeConfig.PAYMENT_APP_URL, '_blank');
              }}
              disabled={isReadOnlyUser}
            >
              <AiOutlinePlusCircle />
              <Styled.ButtonLabel>
                {i18n['Add Payment Method']}
              </Styled.ButtonLabel>
            </Button>
          </Styled.AddButtonWrapper>
        )}
        <Styled.ButtonWrapper>
          {isPaymentOptionAvailable(paymentMethods) && (
            <>
              {isPayByInvoiceNotEligible(doctorProfile?.country) ? (
                <Button
                  onClick={() => {
                    window.open(publicRuntimeConfig.PAYMENT_APP_URL, '_blank');
                  }}
                  variant="secondary"
                  disabled={isReadOnlyUser}
                >
                  <AiOutlinePlusCircle />
                  <Styled.ButtonLabel>
                    {i18n['Add Payment Method']}
                  </Styled.ButtonLabel>
                </Button>
              ) : null}
              <Styled.ModalFooter>
                <Button size="md" variant="secondary" onClick={onClose}>
                  {i18n['Cancel']}
                </Button>
                <Button
                  size="md"
                  onClick={handleUpdateSubscriptionPaymentMethod}
                  disabled={
                    updateSubscriptionPaymentMutation.isLoading ||
                    (!accountDetails?.isDso && !selectedCreditCardToken)
                  }
                  loading={updateSubscriptionPaymentMutation.isLoading}
                >
                  {i18n['Confirm']}
                </Button>
              </Styled.ModalFooter>
            </>
          )}
        </Styled.ButtonWrapper>
      </Styled.CartItemContainer>
    </Modal>
  );
};

export default ChangeSubscriptionPaymentModal;
